import { useState, DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, GripVertical } from 'lucide-react';
import { useDeals, useUpdateDeal } from '@/lib/hooks';
import { formatCurrency, formatPercentage, cn } from '@/lib/utils';
import {
  DealStage,
  TransactionStructure,
  transactionStructureLabels,
  transactionStructureIcons,
  temperatureLabels,
  temperatureColors,
  type DealStageType,
  type TransactionStructureType,
  type Deal,
} from '@/types/database';

const stages: { id: DealStageType; name: string; borderColor: string; bgColor: string }[] = [
  { id: DealStage.SOURCED, name: 'Sourced', borderColor: 'border-t-[hsl(var(--color-stage-sourced))]', bgColor: 'bg-[hsl(var(--color-stage-sourced-bg))]' },
  { id: DealStage.LOI_SENT, name: 'LOI Sent', borderColor: 'border-t-[hsl(var(--color-stage-loi))]', bgColor: 'bg-[hsl(var(--color-stage-loi-bg))]' },
  { id: DealStage.DUE_DILIGENCE, name: 'Due Diligence', borderColor: 'border-t-[hsl(var(--color-stage-dd))]', bgColor: 'bg-[hsl(var(--color-stage-dd-bg))]' },
  { id: DealStage.GP_CONSENT, name: 'GP Consent', borderColor: 'border-t-[hsl(var(--color-stage-consent))]', bgColor: 'bg-[hsl(var(--color-stage-consent-bg))]' },
  { id: DealStage.DOCUMENTATION, name: 'Documentation', borderColor: 'border-t-[hsl(var(--color-stage-documentation))]', bgColor: 'bg-[hsl(var(--color-stage-documentation-bg))]' },
  { id: DealStage.CLOSING, name: 'Closing', borderColor: 'border-t-[hsl(var(--color-stage-closing))]', bgColor: 'bg-[hsl(var(--color-stage-closing-bg))]' },
];

type StructureFilter = 'all' | TransactionStructureType;

export function PipelinePage(): React.ReactElement {
  const [structureFilter, setStructureFilter] = useState<StructureFilter>('all');
  const [draggedDeal, setDraggedDeal] = useState<Deal | null>(null);
  const [dragOverStage, setDragOverStage] = useState<DealStageType | null>(null);
  const { data, isLoading, error, refetch } = useDeals({ limit: 100 });
  const updateDeal = useUpdateDeal();

  const allDeals = data?.data ?? [];
  const filteredDeals = structureFilter === 'all'
    ? allDeals
    : allDeals.filter(d => d.transaction_structure === structureFilter);

  const getDealsByStage = (stage: DealStageType): Deal[] => {
    return filteredDeals.filter(deal => deal.stage === stage);
  };

  const getTotalValueByStage = (stage: DealStageType): number => {
    return getDealsByStage(stage).reduce((sum, deal) => sum + deal.target_value, 0);
  };

  // Drag handlers
  const handleDragStart = (e: DragEvent<HTMLDivElement>, deal: Deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', deal.id);
  };

  const handleDragEnd = () => {
    setDraggedDeal(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>, stage: DealStageType) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>, newStage: DealStageType) => {
    e.preventDefault();
    setDragOverStage(null);

    if (!draggedDeal) return;
    if (draggedDeal.stage === newStage) {
      setDraggedDeal(null);
      return;
    }

    try {
      await updateDeal.mutateAsync({
        id: draggedDeal.id,
        data: { stage: newStage }
      });
      // Refetch to get updated data including activity log
      await refetch();
    } catch (err) {
      console.error('Failed to update deal stage:', err);
    }

    setDraggedDeal(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-negative font-medium">Failed to load pipeline data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Pipeline</h2>
          <p className="text-text-secondary">Drag deals between stages to update their status</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-secondary font-medium">Filter:</span>
          <select
            value={structureFilter}
            onChange={(e) => setStructureFilter(e.target.value as StructureFilter)}
            className="px-3 py-2 border border-border rounded-md text-sm bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Structures</option>
            {Object.entries(TransactionStructure).map(([key, value]) => (
              <option key={key} value={value}>
                {transactionStructureIcons[value]} {transactionStructureLabels[value]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Pipeline Summary */}
      <div className="bg-surface rounded-lg border border-border p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <p className="text-sm text-text-secondary font-medium">Total Deals</p>
              <p className="text-xl font-bold text-text-primary">{filteredDeals.length}</p>
            </div>
            <div className="h-8 w-px bg-border" />
            <div>
              <p className="text-sm text-text-secondary font-medium">Pipeline Value</p>
              <p className="text-xl font-bold text-text-primary">
                {formatCurrency(filteredDeals.reduce((sum, d) => sum + d.target_value, 0))}
              </p>
            </div>
            <div className="h-8 w-px bg-border" />
            <div>
              <p className="text-sm text-text-secondary font-medium">Avg TRUE Discount</p>
              <p className="text-xl font-bold text-accent">
                {filteredDeals.filter(d => d.true_discount).length > 0
                  ? formatPercentage(
                      filteredDeals.filter(d => d.true_discount).reduce((sum, d) => sum + (d.true_discount ?? 0), 0) /
                      filteredDeals.filter(d => d.true_discount).length
                    )
                  : '-'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            {Object.entries(TransactionStructure).map(([key, value]) => {
              const count = filteredDeals.filter(d => d.transaction_structure === value).length;
              return (
                <div key={key} className="flex items-center gap-1">
                  <span>{transactionStructureIcons[value]}</span>
                  <span className="text-text-secondary font-semibold">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      {filteredDeals.length === 0 ? (
        <div className="bg-surface rounded-lg border border-border p-12 text-center shadow-sm">
          <p className="text-text-tertiary mb-2 font-medium">No deals in pipeline</p>
          <p className="text-sm text-text-tertiary">
            Create a new deal or run the Deal Sourcing workflow to find opportunities
          </p>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((stage) => {
            const stageDeals = getDealsByStage(stage.id);
            const stageValue = getTotalValueByStage(stage.id);
            const isDropTarget = dragOverStage === stage.id && draggedDeal?.stage !== stage.id;

            return (
              <div
                key={stage.id}
                className={cn(
                  'flex-shrink-0 w-80 border-t-4 rounded-lg transition-all duration-200 shadow-sm',
                  stage.borderColor,
                  isDropTarget ? 'bg-primary-subtle ring-2 ring-primary' : stage.bgColor
                )}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Column Header */}
                <div className="p-4 border-b border-border bg-surface/50">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-text-primary">{stage.name}</h3>
                    <span className="px-2 py-1 bg-surface rounded-full text-xs font-semibold text-text-secondary border border-border shadow-xs">
                      {stageDeals.length}
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary font-medium mt-1">{formatCurrency(stageValue)}</p>
                </div>

                {/* Deal Cards */}
                <div className="p-3 space-y-3 min-h-[200px]">
                  {stageDeals.map((deal) => (
                    <div
                      key={deal.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, deal)}
                      onDragEnd={handleDragEnd}
                      className={cn(
                        'bg-surface rounded-lg border border-border p-4 shadow-sm cursor-grab active:cursor-grabbing transition-all',
                        draggedDeal?.id === deal.id ? 'opacity-50 ring-2 ring-primary' : 'hover:shadow-md',
                        updateDeal.isPending && updateDeal.variables?.id === deal.id ? 'opacity-50' : ''
                      )}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-text-tertiary" />
                          <span className="text-lg">{transactionStructureIcons[deal.transaction_structure as TransactionStructureType]}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={cn(
                            'px-2 py-0.5 text-xs font-medium rounded-full border',
                            temperatureColors[deal.temperature as keyof typeof temperatureColors]
                          )}>
                            {temperatureLabels[deal.temperature as keyof typeof temperatureLabels]}
                          </span>
                          <span className="text-xs font-bold text-text-primary bg-muted px-1.5 py-0.5 rounded">
                            {deal.closing_probability ?? 50}%
                          </span>
                        </div>
                      </div>
                      <Link to={`/deals/${deal.id}`} className="block">
                        <h4 className="font-semibold text-text-primary text-sm mb-1 line-clamp-2 hover:text-primary transition-colors">
                          {deal.name}
                        </h4>
                      </Link>
                      <p className="text-xs text-text-tertiary mb-3">{deal.seller_name}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-text-primary">
                          {formatCurrency(deal.target_value)}
                        </span>
                        {deal.true_discount && (
                          <span className="text-sm font-semibold text-accent">
                            {formatPercentage(deal.true_discount)}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-1 text-xs text-text-tertiary">
                        <span>{transactionStructureLabels[deal.transaction_structure as TransactionStructureType]}</span>
                        {deal.partial_percentage && (
                          <span className="font-semibold">({(deal.partial_percentage * 100).toFixed(0)}%)</span>
                        )}
                      </div>
                    </div>
                  ))}

                  {stageDeals.length === 0 && (
                    <div className={cn(
                      "text-center py-8 rounded-lg border-2 border-dashed transition-colors",
                      isDropTarget ? 'border-primary bg-primary-subtle' : 'border-border'
                    )}>
                      <p className="text-sm text-text-tertiary">
                        {isDropTarget ? 'Drop here' : 'No deals'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
